import React from "react";
import { filter, map } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faEnvelope,
  faMobile,
} from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";
import Select from "components/shared/form/fields/Select";
import RadioButton from "components/shared/form/fields/RadioButton";
import AddressField from "components/shared/form/fields/AddressField";
import NumberField from "components/shared/form/fields/NumberFiield";
import TextInput from "components/shared/form/fields/TextInput";
import CheckboxFieldWrapper from "components/shared/form/fields/CheckboxFieldWrapper";
import Checkbox from "components/shared/form/fields/Checkbox";

export default function MemberSettings() {
  const twoFactorOptions = [
    {
      value: "disabled",
      label: I18n.t("js.administration.authentication.2fa.deactivated"),
    },
    {
      value: "enabled",
      label: I18n.t("js.administration.authentication.2fa.optional"),
    },
  ];

  if (Tixxt.currentNetwork.getConfig("two_factor.enforced_available"))
    twoFactorOptions.push({
      value: "enforced",
      label: I18n.t(
        "js.administration.authentication.2fa.enforced_for_network",
      ),
    });

  const { watch } = useFormContext();
  const maps_enabled = watch("maps_enabled");
  const show_map = watch("show_map");
  const map_scope_selection = watch("map_scope_selection");
  const two_factor = watch("two_factor");
  const deleted_membership_count = watch("deleted_membership_count");
  const anonymize_memberships_managed_by_admin = watch(
    "anonymize_memberships_managed_by_admin",
  );
  const show_locked_memberships_managed_by_admin = watch(
    "show_locked_memberships_managed_by_admin",
  );
  const initial_anonymize_memberships_config = watch(
    "initial.anonymize_memberships_config",
  );
  const anonymize_memberships_config = watch("anonymize_memberships_config");
  const anonymize_memberships_after_days = watch(
    "anonymize_memberships_after_days",
  );

  const available_factor_types = {
    two_factor_totp_available: watch("two_factor_totp_available"),
    two_factor_email_available: watch("two_factor_email_available"),
  };

  return (
    <>
      <h3>
        {I18n.t("js.administration.network_settings.member_settings.title")}
      </h3>

      <h4 className="font-bold">
        {I18n.t("js.administration.network_settings.member_settings.directory")}
      </h4>

      <div className="form-horizontal flex flex-col gap-4">
        <RadioButton
          name="directory_default_sort"
          label={I18n.t("js.networks.directory_sort.label")}
          options={[
            {
              value: "first_name",
              label: I18n.t("js.networks.directory_sort.first_name"),
            },
            {
              value: "last_name",
              label: I18n.t("js.networks.directory_sort.last_name"),
            },
          ]}
          required
        />

        {maps_enabled === "enabled" && (
          <>
            <div>
              <CheckboxFieldWrapper
                label={I18n.t(
                  "js.administration.network_settings.member_settings.map.label",
                )}
              >
                <div className="flex flex-col gap-2 divide-y divide-neutral">
                  <div className="flex align-baseline gap-2 mb-3 mt-1">
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className="text-3xl text-muted"
                    />
                    <div className="help-block">
                      {I18n.t("js.administration.network_settings.map_hint")}
                    </div>
                  </div>
                  <RadioButton
                    name="show_map"
                    options={[
                      {
                        value: "enabled",
                        label: I18n.t(
                          "js.administration.network_settings.member_settings.map.enabled",
                        ),
                      },
                      {
                        value: "disabled",
                        label: I18n.t(
                          "js.administration.network_settings.member_settings.map.disabled",
                        ),
                      },
                    ]}
                    hint={I18n.t(
                      "js.administration.network_settings.map_modal_body",
                    )}
                    required
                    withoutWrapper
                  />
                  {show_map === "enabled" && (
                    <div>
                      <RadioButton
                        name="map_scope_selection"
                        label={I18n.t(
                          "js.administration.network_settings.member_settings.map.map_scope_selection.label",
                        )}
                        options={[
                          {
                            value: "all",
                            label: I18n.t(
                              "js.administration.network_settings.member_settings.map.map_scope_selection.all",
                            ),
                          },
                          {
                            value: "poi",
                            label: I18n.t(
                              "js.administration.network_settings.member_settings.map.map_scope_selection.poi",
                            ),
                          },
                        ]}
                        withoutWrapper
                      />
                      {map_scope_selection === "poi" && (
                        <div className="pt-1 flex flex-col gap-4">
                          <AddressField
                            name="network_location"
                            required={{
                              street: true,
                              number: true,
                              zip: true,
                              city: true,
                            }}
                          />
                          <Select
                            name="zoom_level"
                            label={I18n.t(
                              "js.administration.network_settings.member_settings.map.map_scope_selection.zoom",
                            )}
                            options={[
                              {
                                value: "0",
                                label: I18n.t(
                                  "js.administration.network_settings.member_settings.map.map_scope_selection.earth",
                                ),
                              },
                              {
                                value: "3",
                                label: I18n.t(
                                  "js.administration.network_settings.member_settings.map.map_scope_selection.continent",
                                ),
                              },
                              {
                                value: "6",
                                label: I18n.t(
                                  "js.administration.network_settings.member_settings.map.map_scope_selection.rivers",
                                ),
                              },
                              {
                                value: "10",
                                label: I18n.t(
                                  "js.administration.network_settings.member_settings.map.map_scope_selection.roads",
                                ),
                              },
                              {
                                value: "15",
                                label: I18n.t(
                                  "js.administration.network_settings.member_settings.map.map_scope_selection.buildings",
                                ),
                              },
                            ]}
                          />
                        </div>
                      )}
                      <RadioButton
                        name="map_theme"
                        label={I18n.t(
                          "js.administration.network_settings.member_settings.map.theme.label",
                        )}
                        options={[
                          {
                            value: "alidade_smooth",
                            label: (
                              <img
                                className="w-56 pb-1"
                                src="/static/map/alidade_smooth.png"
                                alt="alidade_smooth"
                              />
                            ),
                          },
                          {
                            value: "alidade_smooth_dark",
                            label: (
                              <img
                                className="w-56 pb-1"
                                src="/static/map/alidade_smooth_dark.png"
                                alt="alidade_smooth_dark"
                              />
                            ),
                          },
                          {
                            value: "osm_bright",
                            label: (
                              <img
                                className="w-56 pb-1"
                                src="/static/map/osm_bright.png"
                                alt="osm_bright"
                              />
                            ),
                          },
                          {
                            value: "outdoors",
                            label: (
                              <img
                                className="w-56"
                                src="/static/map/outdoors.png"
                                alt="outdoors"
                              />
                            ),
                          },
                        ]}
                        withoutWrapper
                      />
                    </div>
                  )}
                </div>
              </CheckboxFieldWrapper>
            </div>
          </>
        )}
        <Select
          name="directory_profile_fields"
          label={I18n.t("js.networks.directory_fields.label")}
          options={map(
            Preload.current_network.profile_fields as Array<{
              name: string;
              label: string;
            }>,
            (field) => ({
              value: field.name,
              label: field.label,
            }),
          )}
          hint={I18n.t("js.networks.directory_fields.help")}
          multi
        />
        <Select
          name="detection_profile_field"
          label={I18n.t("js.networks.detection_profile_field.label")}
          options={map(
            Preload.current_network.profile_fields_without_multiline as Array<{
              name: string;
              label: string;
            }>,
            (field) => ({
              value: field.name,
              label: field.label,
            }),
          )}
          hint={I18n.t("js.networks.detection_profile_field.hint")}
        />

        <Select
          name="directory_filter_profile_fields"
          label={I18n.t("js.networks.directory_filter_profile_fields.label")}
          options={map(
            filter(Preload.current_network.profile_fields, {
              type: "selection",
            }),
            (field) => ({
              value: field.name,
              label: field.label,
            }),
          )}
          multi
        />
        {show_locked_memberships_managed_by_admin && (
          <Select
            name="show_locked_memberships"
            label={"Gesperrte Nutzer im Nutzerverzeichnis anzeigen?"}
            options={[
              {
                value: "enabled",
                label: I18n.t("js.administration.network_settings.choices.yes"),
              },
              {
                value: "disabled",
                label: I18n.t("js.administration.network_settings.choices.no"),
              },
            ]}
            required
          />
        )}

        {anonymize_memberships_managed_by_admin && (
          <>
            {["enabled", "after_days"].includes(anonymize_memberships_config) &&
              initial_anonymize_memberships_config == "disabled" && (
                <div className="alert alert-error">
                  {I18n.t(
                    "js.administration.network_settings.anonymize_memberships.warning",
                    { deletedMemberCount: deleted_membership_count },
                  )}
                </div>
              )}
            <RadioButton
              name="anonymize_memberships_config"
              label={I18n.t(
                "js.administration.network_settings.anonymize_memberships.title",
              )}
              options={[
                {
                  value: "enabled",
                  label: I18n.t(
                    "js.administration.network_settings.anonymize_memberships.enabled",
                  ),
                },
                {
                  value: "after_days",
                  label: I18n.t(
                    "js.administration.network_settings.anonymize_memberships.after_x_days",
                    { days: anonymize_memberships_after_days },
                  ),
                },
                {
                  value: "disabled",
                  label: I18n.t(
                    "js.administration.network_settings.anonymize_memberships.disabled",
                  ),
                },
              ]}
              required
            />
            {anonymize_memberships_config === "after_days" && (
              <NumberField
                label={I18n.t(
                  "js.administration.network_settings.anonymize_memberships.days.title",
                )}
                name="anonymize_memberships_after_days"
                required
              />
            )}
            {["enabled", "after_days"].includes(
              anonymize_memberships_config as string,
            ) && (
              <TextInput
                label={I18n.t(
                  "js.administration.network_settings.anonymize_memberships.name.title",
                )}
                name="anonymize_memberships_name"
                required
              />
            )}
          </>
        )}
        {Tixxt.currentNetwork.getConfig("guest_users.allowed") && (
          <h4 className="font-bold">
            {I18n.t(
              "js.administration.network_settings.guest_users_settings.title",
            )}
          </h4>
        )}
        <h4 className="font-bold">
          {I18n.t(
            "js.administration.network_settings.member_settings.security",
          )}
        </h4>

        <RadioButton
          name="two_factor"
          label={I18n.t("js.authentication.2fa.page_title")}
          options={twoFactorOptions}
          hint={
            Tixxt.currentNetwork.getConfig("two_factor.enforced_available")
              ? I18n.t(
                  "js.administration.authentication.2fa.enforced_description",
                )
              : undefined
          }
          required
          extraElement={
            two_factor != "disabled" ? (
              <>
                <div className="grid grid-cols-2 gap-2 w-full mt-2">
                  <div className="flex gap-1">
                    <Checkbox name="two_factor_totp_available" label="" />
                    <div key="totp" className="border-box flex-1 p-2">
                      <div className="media-body flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                          <FontAwesomeIcon icon={faMobile} size="2xl" />
                          <div className="flex grow justify-between gap-2 items-center">
                            <span className="font-semibold">
                              {I18n.t(
                                "js.authentication.2fa.authenticator_app",
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Checkbox
                      name="two_factor_email_available"
                      label=""
                      required={
                        available_factor_types?.two_factor_totp_available ===
                        false
                          ? I18n.t(
                              "js.administration.authentication.2fa.selection_required",
                            )
                          : false
                      }
                    />
                    <div key="email" className="border-box flex-1 p-2">
                      <div className="media-body flex flex-col gap-2">
                        <div className="flex gap-2 items-center">
                          <FontAwesomeIcon icon={faEnvelope} size="2xl" />
                          <div className="flex grow justify-between gap-2 items-center">
                            <span className="font-semibold">
                              {I18n.t("js.authentication.2fa.email")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!available_factor_types?.two_factor_totp_available &&
                  !available_factor_types?.two_factor_email_available && (
                    <div className="text-sm text-danger">
                      {I18n.t(
                        "js.administration.authentication.2fa.selection_required",
                      )}
                    </div>
                  )}
              </>
            ) : undefined
          }
        />
      </div>
    </>
  );
}
